import "./Dashboard.css";
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuthStore } from "../../../Services/authStore";
import { toastError } from "../../../Services/toaster";

// ─────────────────────────────────────────────────────────
//  STATUS COLOR MAPS
// ─────────────────────────────────────────────────────────
const PO_STATUS_COLORS = {
  Draft: "#9F8E87",
  Submitted: "#3B82C4",
  Approved: "#1E8449",
  Rejected: "#C0392B",
  Cancelled: "#7F8C8D",
};

const INVOICE_STATUS_COLORS = {
  Draft: "#9F8E87",
  Submitted: "#3B82C4",
  Approved: "#1E8449",
  Paid: "#0F9D8C",
  Cancelled: "#7F8C8D",
};

const PAYMENT_STATUS_COLORS = {
  Draft: "#9F8E87",
  Submitted: "#3B82C4",
  Approved: "#1E8449",
  Paid: "#0F9D8C",
  Cancelled: "#7F8C8D",
};

// ─────────────────────────────────────────────────────────
//  FORMAT HELPERS
// ─────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const timeAgo = (date) => {
  if (!date) return "-";
  const diffMs = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(date);
};

// ─────────────────────────────────────────────────────────
//  SMALL REUSABLE COMPONENTS
// ─────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, accent }) {
  return (
    <div className="dash-kpi-card" style={{ "--accent": accent }}>
      <div className="dash-kpi-icon">{icon}</div>
      <div className="dash-kpi-info">
        <div className="dash-kpi-value">{value}</div>
        <div className="dash-kpi-label">{label}</div>
        {sub && <div className="dash-kpi-sub">{sub}</div>}
      </div>
    </div>
  );
}

function DonutChart({ title, data, size = 140, strokeWidth = 16 }) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;

  return (
    <div className="dash-donut-card">
      <div className="dash-donut-header">{title}</div>
      {total === 0 ? (
        <div className="dash-donut-empty">No data yet</div>
      ) : (
        <div className="dash-donut-body">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#F3ECE7"
              strokeWidth={strokeWidth}
            />
            {data.map((d, i) => {
              const fraction = d.value / total;
              const dash = fraction * circumference;
              const dashArray = `${dash} ${circumference - dash}`;
              const dashOffset = -cumulative;
              cumulative += dash;
              return (
                <circle
                  key={i}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={d.color}
                  strokeWidth={strokeWidth}
                  strokeDasharray={dashArray}
                  strokeDashoffset={dashOffset}
                  transform={`rotate(-90 ${size / 2} ${size / 2})`}
                  className="dash-donut-segment"
                />
              );
            })}
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dy="0.35em"
              className="dash-donut-total"
            >
              {total}
            </text>
          </svg>
          <div className="dash-donut-legend">
            {data.map((d, i) => (
              <div key={i} className="dash-legend-item">
                <span
                  className="dash-legend-dot"
                  style={{ background: d.color }}
                ></span>
                <span className="dash-legend-label">{d.label}</span>
                <span className="dash-legend-value">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MonthlySpendChart({ data }) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="dash-bar-card">
      <div className="dash-donut-header">Spend Trend (6 months)</div>
      {data.every((d) => d.value === 0) ? (
        <div className="dash-donut-empty">No payment activity yet</div>
      ) : (
        <div className="dash-bar-chart">
          {data.map((d, i) => (
            <div className="dash-bar-col" key={i}>
              <div className="dash-bar-value">
                {d.value > 0 ? formatCurrency(d.value) : ""}
              </div>
              <div className="dash-bar-track">
                <div
                  className="dash-bar-fill"
                  style={{ height: `${Math.max((d.value / max) * 100, 2)}%` }}
                ></div>
              </div>
              <div className="dash-bar-label">{d.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status, colorMap }) {
  const color = colorMap[status] || "#9F8E87";
  return (
    <span
      className="dash-pill"
      style={{
        color: color,
        background: `${color}1A`,
        borderColor: `${color}40`,
      }}
    >
      {status || "-"}
    </span>
  );
}

const TYPE_ICON = { PO: "📋", Invoice: "📄", Payment: "💳" };

// ─────────────────────────────────────────────────────────
//  MAIN DASHBOARD
// ─────────────────────────────────────────────────────────
export default function Dashboard() {
  const [pos, setPos] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    accept: "application/json",
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [poRes, invRes, payRes] = await Promise.all([
        axios.get(`${API_URL}/api/po/`, { headers: authHeaders }),
        axios.get(`${API_URL}/api/invoice/`, {
          headers: authHeaders,
          params: { skip: 0, limit: 100 },
        }),
        axios.get(`${API_URL}/api/payment/`, {
          headers: authHeaders,
          params: { skip: 0, limit: 100 },
        }),
      ]);
      setPos(poRes.data?.data || poRes.data || []);
      setInvoices(invRes.data?.data || invRes.data || []);
      setPayments(payRes.data?.data || payRes.data || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
      toastError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Derived Metrics ────────────────────────────────────

  const poStatusData = useMemo(() => {
    const counts = {};
    pos.forEach((p) => {
      const s = p.status && PO_STATUS_COLORS[p.status] ? p.status : "Draft";
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).map(([label, value]) => ({
      label,
      value,
      color: PO_STATUS_COLORS[label] || "#9F8E87",
    }));
  }, [pos]);

  const invoiceStatusData = useMemo(() => {
    const counts = {};
    invoices.forEach((inv) => {
      const s =
        inv.invoice_status && INVOICE_STATUS_COLORS[inv.invoice_status]
          ? inv.invoice_status
          : "Draft";
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).map(([label, value]) => ({
      label,
      value,
      color: INVOICE_STATUS_COLORS[label] || "#9F8E87",
    }));
  }, [invoices]);

  const paymentStatusData = useMemo(() => {
    const counts = {};
    payments.forEach((p) => {
      const s =
        p.payment_status && PAYMENT_STATUS_COLORS[p.payment_status]
          ? p.payment_status
          : "Draft";
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).map(([label, value]) => ({
      label,
      value,
      color: PAYMENT_STATUS_COLORS[label] || "#9F8E87",
    }));
  }, [payments]);

  const totalPOValue = useMemo(
    () => pos.reduce((sum, p) => sum + (parseFloat(p.doc_total) || 0), 0),
    [pos],
  );

  const totalInvoiceValue = useMemo(
    () =>
      invoices.reduce(
        (sum, inv) => sum + (parseFloat(inv.invoice_total) || 0),
        0,
      ),
    [invoices],
  );

  const totalPaid = useMemo(
    () =>
      payments
        .filter((p) => p.payment_status !== "Cancelled")
        .reduce((sum, p) => sum + (parseFloat(p.payment_amount) || 0), 0),
    [payments],
  );

  const invoicePaidMap = useMemo(() => {
    const map = {};
    payments
      .filter((p) => p.payment_status !== "Cancelled")
      .forEach((p) => {
        map[p.invoice_id] =
          (map[p.invoice_id] || 0) + (parseFloat(p.payment_amount) || 0);
      });
    return map;
  }, [payments]);

  const totalOutstanding = useMemo(() => {
    return invoices.reduce((sum, inv) => {
      const total = parseFloat(inv.invoice_total) || 0;
      const paid = invoicePaidMap[inv.id] || 0;
      const remaining = total - paid;
      return sum + (remaining > 0 ? remaining : 0);
    }, 0);
  }, [invoices, invoicePaidMap]);

  const pendingApprovalsCount = useMemo(() => {
    const poPending = pos.filter((p) => p.approval_status === "Pending").length;
    const payPending = payments.filter(
      (p) => p.approval_status === "Pending",
    ).length;
    return poPending + payPending;
  }, [pos, payments]);

  const approvalQueue = useMemo(() => {
    const poPending = pos
      .filter((p) => p.approval_status === "Pending")
      .map((p) => ({
        type: "PO",
        number: p.po_number,
        title: p.vendor_name,
        amount: p.doc_total,
        date: p.created_at,
      }));
    const payPending = payments
      .filter((p) => p.approval_status === "Pending")
      .map((p) => ({
        type: "Payment",
        number: p.payment_number,
        title: p.invoice_number,
        amount: p.payment_amount,
        date: p.created_at,
      }));
    return [...poPending, ...payPending]
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 6);
  }, [pos, payments]);

  const recentActivity = useMemo(() => {
    const poActs = pos.map((p) => ({
      type: "PO",
      number: p.po_number,
      title: p.vendor_name,
      amount: p.doc_total,
      date: p.created_at,
      status: p.status,
      colorMap: PO_STATUS_COLORS,
    }));
    const invActs = invoices.map((inv) => ({
      type: "Invoice",
      number: inv.invoice_number,
      title: inv.vendor_name,
      amount: inv.invoice_total,
      date: inv.created_at,
      status: inv.invoice_status,
      colorMap: INVOICE_STATUS_COLORS,
    }));
    const payActs = payments.map((p) => ({
      type: "Payment",
      number: p.payment_number,
      title: p.invoice_number,
      amount: p.payment_amount,
      date: p.created_at,
      status: p.payment_status,
      colorMap: PAYMENT_STATUS_COLORS,
    }));
    return [...poActs, ...invActs, ...payActs]
      .filter((a) => a.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 8);
  }, [pos, invoices, payments]);

  const monthlySpend = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${d.getMonth()}`,
        label: d.toLocaleDateString("en-US", { month: "short" }),
        value: 0,
      });
    }
    payments
      .filter((p) => p.payment_status !== "Cancelled")
      .forEach((p) => {
        if (!p.payment_date) return;
        const d = new Date(p.payment_date);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        const bucket = months.find((m) => m.key === key);
        if (bucket) bucket.value += parseFloat(p.payment_amount) || 0;
      });
    return months;
  }, [payments]);

  // ─── Render ──────────────────────────────────────────────

  return (
    <div className="dash-wrapper">
      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">Procurement Dashboard</h1>
          <p className="dash-page-subtitle">
            A live overview of purchase orders, invoices, and payments
          </p>
        </div>
        <div className="dash-header-right">
          {lastUpdated && (
            <span className="dash-last-updated">
              Updated {timeAgo(lastUpdated)}
            </span>
          )}
          <button
            className="dash-refresh-btn"
            onClick={fetchAll}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "⟳ Refresh"}
          </button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="dash-kpi-grid">
        <KpiCard
          icon="📋"
          label="Purchase Orders"
          value={pos.length}
          sub={formatCurrency(totalPOValue)}
          accent="#A27B5B"
        />
        <KpiCard
          icon="📄"
          label="Invoices"
          value={invoices.length}
          sub={formatCurrency(totalInvoiceValue)}
          accent="#3B82C4"
        />
        <KpiCard
          icon="💳"
          label="Total Paid"
          value={formatCurrency(totalPaid)}
          sub={`${payments.length} payments`}
          accent="#0F9D8C"
        />
        <KpiCard
          icon="⚠️"
          label="Outstanding Balance"
          value={formatCurrency(totalOutstanding)}
          sub="Across open invoices"
          accent="#C0392B"
        />
        <KpiCard
          icon="⏳"
          label="Pending Approvals"
          value={pendingApprovalsCount}
          sub="POs & payments"
          accent="#B7950B"
        />
      </div>

      {/* Charts Row */}
      <div className="dash-charts-grid">
        <DonutChart title="PO Status" data={poStatusData} />
        <DonutChart title="Invoice Status" data={invoiceStatusData} />
        <DonutChart title="Payment Status" data={paymentStatusData} />
        <MonthlySpendChart data={monthlySpend} />
      </div>

      {/* Bottom Grid: Approval Queue + Recent Activity */}
      <div className="dash-bottom-grid">
        <div className="dash-panel">
          <div className="dash-panel-header">
            <h3>Awaiting Approval</h3>
            <span className="dash-panel-count">{approvalQueue.length}</span>
          </div>
          {approvalQueue.length === 0 ? (
            <div className="dash-empty-panel">🎉 Nothing pending right now</div>
          ) : (
            <div className="dash-queue-list">
              {approvalQueue.map((item, i) => (
                <div className="dash-queue-row" key={i}>
                  <div className="dash-queue-icon">{TYPE_ICON[item.type]}</div>
                  <div className="dash-queue-info">
                    <div className="dash-queue-number">
                      {item.number}
                      <span className="dash-queue-type">{item.type}</span>
                    </div>
                    <div className="dash-queue-title">{item.title || "-"}</div>
                  </div>
                  <div className="dash-queue-right">
                    <div className="dash-queue-amount">
                      {formatCurrency(item.amount)}
                    </div>
                    <div className="dash-queue-date">{timeAgo(item.date)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dash-panel">
          <div className="dash-panel-header">
            <h3>Recent Activity</h3>
          </div>
          {recentActivity.length === 0 ? (
            <div className="dash-empty-panel">No activity yet</div>
          ) : (
            <div className="dash-activity-list">
              {recentActivity.map((item, i) => (
                <div className="dash-activity-row" key={i}>
                  <div className="dash-activity-icon">
                    {TYPE_ICON[item.type]}
                  </div>
                  <div className="dash-activity-info">
                    <div className="dash-activity-top">
                      <span className="dash-activity-number">
                        {item.number}
                      </span>
                      <StatusPill
                        status={item.status}
                        colorMap={item.colorMap}
                      />
                    </div>
                    <div className="dash-activity-title">
                      {item.title || "-"}
                    </div>
                  </div>
                  <div className="dash-activity-right">
                    <div className="dash-activity-amount">
                      {formatCurrency(item.amount)}
                    </div>
                    <div className="dash-activity-date">
                      {timeAgo(item.date)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
